/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { Grid, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import PopupTopBar from './PopupTopBar';
import Steps from '../../common/Steps';
import ContestInfo from './ContestInfo';
import AddDealerships from './AddDealerships';
import RoleConfiguration from './RoleConfiguration';
import { showProgress } from '../../../redux/actions/DealershipActions';
import Service from '../../../apis/service';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import {
  convertToFormData,
  formatDate,
  scoreIsDisplayed,
  scoreParamEnabled,
  scoreParamLogic,
  transformData,
  typeLabel,
  updateArrayState,
  validateStepOne,
} from '../utils';
import useFetchDepartments from '../hooks/useFetchDepartments';
import PopupFooter from './PopupFooter';
import BottomSheetContainer from './BottomSheetContainer';

const createCustomStyles = (height) => ({
  content: {
    height: `${height}px`,
    width: '1070px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0px',
  },
});
const useStyles = makeStyles(() => ({
  popupMainContainer: {
    background: 'rgba(242, 244, 247, 1)',
    width: '100%',
  },
  popupStepsContainer: {
    position: 'sticky',
    top: '85px',
    zIndex: 10,
    padding: '20px',
    background: 'rgba(255, 255, 255, 1)',
    width: '100%',
  },
}));

const ContestAddNewPopup = ({ close, add, selectedData, refreshData }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { departmentData } = useFetchDepartments();
  const startDate = formatDate(new Date());
  const endDate = null;

  const [data, setData] = useState({
    logo: null,
    csvFile: null,
    visibility: [],
    enabled: true,
    scoreBreakupParameters: [],
    departmentRoleIds: [],
    dealerships: [],
    startDate,
    endDate,
    timezone: 'America/Denver',
    category: 'IMPORT',
  });

  const contestList = useSelector((state) => state.contestData.contestList);
  const contestDetails = useSelector((state) => state.contestData.selectedContestDetails);
  const networkPartnerList = useSelector((state) => state.networkPartnerData.networkPartnerList);

  const [step, setStep] = useState(1);
  const [filters, setFilters] = useState({});
  const [filterObjectInitial, setFilterObjectInitial] = useState({});
  const [logoFilename, setLogoFilename] = useState('');
  const [csvFilename, setCsvFilename] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [isSheetOpen, setSheetOpen] = useState(false);
  const [enableNetworkPartnerID, setEnableNetworkPartnerID] = useState(true);
  const [metaColumns, setMetaColumns] = useState(null);
  const [department, setDepartment] = useState([]);
  const modalRef = useRef(null);
  useEffect(() => {
    if (step === 1) modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step, modalRef.current]);

  useEffect(() => {
    if (departmentData) setDepartment(departmentData);
  }, [departmentData]);

  useEffect(() => {
    if (!add) {
      const { category, ranking, highlightTopScoreLogic, dealerships, scoreBreakupParameters } = selectedData;
      const newData = {
        ...selectedData,
        ranking: category !== 'DEFAULT' ? ranking[0] : undefined,
        highlightTopScoreLogic: highlightTopScoreLogic ? JSON.stringify(highlightTopScoreLogic) : '',
        dealerships: dealerships?.map((item) => item.id),
      };
      setData(newData);
      if (category === 'IMPORT') {
        setMetaColumns(scoreBreakupParameters);
      }
    }
  }, [add, selectedData]);

  useEffect(() => {
    if (add) {
      const roleIds = department?.flatMap((dept) => dept?.roles?.map((role) => role.id));
      setData((prevData) => ({ ...prevData, departmentRoleIds: roleIds }));
    }
  }, [department]);

  useEffect(() => {
    getFilters();
  }, []);

  const toggleSheet = () => setSheetOpen((prev) => !prev);
  const getMetadatafromCsv = async (fileData) => {
    dispatch(showProgress(true));
    try {
      const formData = new FormData();
      formData.append('csvFile', fileData);
      const res = await Service.post(apiEndpoints.CON_CREATE_CSV_METADATA, formData);
      setMetaColumns(res);
      setData((prevData) => ({ ...prevData, scoreBreakupParameters: res }));
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };
  const getFilters = useCallback(async () => {
    dispatch(showProgress(true));
    try {
      const res = await Service.get(apiEndpoints.DEALER_FILTER);
      const { filterData, filterObject } = transformData(res);
      setFilters(filterData || {});
      setFilterObjectInitial(filterObject || {});
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  }, []);
  const handleFileChange = (name, files, setFilename) => {
    if (files.length > 0) {
      const fileData = files[0];
      setFilename(fileData?.name);
      setData((prevData) => ({
        ...prevData,
        [name]: fileData,
      }));
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (name === 'logo') {
          setThumbnail(ev.target.result);
        }
      };
      reader.readAsDataURL(fileData);
      if (name === 'csvFile') {
        getMetadatafromCsv(fileData);
      }
    }
  };

  const handleDataChange = useCallback(
    ({ target }) => {
      const { name, value, checked, files } = target;
      if (name === 'logo') {
        handleFileChange(name, files, setLogoFilename);
      } else if (name === 'csvFile') {
        handleFileChange(name, files, setCsvFilename);
      } else if (name === 'scoreBreakupParameters' || name?.endsWith('Displayed')) {
        const type = name?.endsWith('Displayed') ? `${name.replace('Displayed', '')} Stars` : value;
        setData((prevData) => ({
          ...prevData,
          scoreBreakupParameters: scoreIsDisplayed(data.scoreBreakupParameters, type, checked),
        }));
      } else if (name?.endsWith('Stars')) {
        const type = name.replace('Stars', '').toLowerCase();
        setData((prevData) => ({
          ...prevData,
          scoreBreakupParameters: scoreParamLogic(data.scoreBreakupParameters, type, value),
        }));
      } else if (name?.endsWith('Enabled')) {
        const type = name.replace('Enabled', '').toLowerCase();
        setData((prevData) => ({
          ...prevData,
          scoreBreakupParameters: scoreParamEnabled(data?.scoreBreakupParameters, type, checked),
        }));
      } else if (name === 'departmentRoleIds' || name === 'dealerships') {
        setData((prevData) => updateArrayState(prevData, name, value, checked));
      } else if (name === 'visibility') {
        const updatedVisibility = checked
          ? [...(data.visibility || []), value]
          : (data.visibility || []).filter((item) => item !== value);
        setData((prevData) => ({ ...prevData, visibility: updatedVisibility }));
      } else if (name === 'columnName' || name === 'orderBy') {
        setData((prevData) => ({ ...prevData, ranking: { ...prevData.ranking, [name]: value } }));
      } else if (name === 'category') {
        const isNetworkPartnerEnabled = value === 2;
        setEnableNetworkPartnerID(isNetworkPartnerEnabled);
        setData((prevData) => ({
          ...prevData,
          [name]: isNetworkPartnerEnabled ? 'IMPORT' : 'DEFAULT',
        }));
      } else if (name === 'type') {
        setData((prevData) => ({
          ...prevData,
          [name]: typeLabel(value),
        }));
      } else {
        setData((prevData) => ({ ...prevData, [name]: value }));
      }
    },
    [data],
  );

  const handleDateChange = (label, date) => setData({ ...data, [label]: date });
  const handleDeleteLogo = () => {
    setData({
      ...data,
      logo: null,
    });
    setLogoFilename('');
  };

  const saveContest = async () => {
    if (step === 1 && !validateStepOne(data, add, contestList)) return;
    setStep((pre) => (pre + 1 !== 4 ? pre + 1 : pre));
    if (step === 3) {
      const formData = convertToFormData(data, add);
      const endPoint = add ? apiEndpoints.CON_CREATE : apiEndpoints.CON_UPDATE.replace('ID', data?.id);
      const msg = add ? 'Contest Added Successfully' : 'Contest Updated Successfully';
      dispatch(showProgress(false));
      try {
        dispatch(showProgress(true));
        await Service.post(endPoint, formData);
        notifySuccess(msg);
        refreshData();
        close(true);
        dispatch(showProgress(false));
      } catch (error) {
        ErrorToast(error);
        dispatch(showProgress(false));
      }
    }
  };
  const backBtn = async () => setStep((pre) => (pre - 1 !== 0 ? pre - 1 : pre));
  const handleDeptChange = (deptId, selected, departments) => {
    setData((prevData) => {
      const selectedDept = departments.find((dept) => dept.id === deptId);
      const roleIds = selectedDept?.roles.map((role) => role.id) || [];
      return {
        ...prevData,
        departmentRoleIds: selected
          ? [...new Set([...prevData.departmentRoleIds, ...roleIds])]
          : prevData.departmentRoleIds.filter((id) => !roleIds.includes(id)),
      };
    });
  };
  const handleSelectAll = (updatedIds, checked) => {
    setData((prevData) => ({
      ...prevData,
      dealerships: checked
        ? [...new Set([...prevData.dealerships, ...updatedIds])]
        : prevData.dealerships.filter((id) => !updatedIds.includes(id)),
    }));
  };
  const handleRemoveDealership = ({ target: { id } }, name) => {
    if (name === 'Remove') {
      setData((prevData) => {
        const updatedIds = prevData.dealerships?.filter((item) => item !== parseInt(id, 10));
        return { ...prevData, dealerships: updatedIds };
      });
    }
  };
  return (
    <Modal
      isOpen
      onRequestClose={() => false}
      shouldCloseOnEsc={false}
      style={step === 3 ? createCustomStyles(825) : createCustomStyles(723)}
    >
      <div ref={modalRef}>
        <PopupTopBar add={add} title="Contest" close={close} />

        <Grid container className={classes.popupMainContainer}>
          <Grid item className={classes.popupStepsContainer}>
            <Steps step={step} stepInfo={['Contest Info', 'Role Configuration', 'Dealerships']} />
          </Grid>

          {step === 1 && (
            <Grid item style={{ minHeight: '628px' }}>
              <ContestInfo
                data={data}
                handleDataChange={handleDataChange}
                handleDateChange={handleDateChange}
                handleDeleteLogo={handleDeleteLogo}
                metaColumns={metaColumns}
                csvFilename={csvFilename}
                logoFilename={logoFilename}
                thumbnail={thumbnail}
                networkPartnerList={networkPartnerList}
                enableNetworkPartnerID={enableNetworkPartnerID}
                add={add}
              />
            </Grid>
          )}
          {step === 2 && (
            <Grid item style={{ minHeight: '471px' }}>
              <RoleConfiguration
                data={data}
                handleChange={handleDataChange}
                departmentRoleIds={contestDetails.departmentRoleIds}
                handleDeptChange={handleDeptChange}
                departmentData={department}
              />
            </Grid>
          )}
          {step === 3 && (
            <Grid item style={{ width: '100%' }}>
              <AddDealerships
                data={data}
                handleDataChange={handleDataChange}
                handleSelectAll={handleSelectAll}
                filters={filters}
                filterObjectInitial={filterObjectInitial}
              />
            </Grid>
          )}
        </Grid>
        {step === 3 && (
          <BottomSheetContainer
            data={data}
            isSheetOpen={isSheetOpen}
            toggleSheet={toggleSheet}
            handleRemove={handleRemoveDealership}
          />
        )}
        <PopupFooter backBtn={backBtn} saveContest={saveContest} step={step} />
      </div>
    </Modal>
  );
};

export default ContestAddNewPopup;
